import { useState } from "react";
import { toast } from "sonner";
import { Award, X, Check } from "lucide-react";
import DatePickerIT from "./ui/date-picker-it";

/**
 * Blocco 4 · F · Modal "Dichiara raggiunta" per meta/azione.
 *
 * Props:
 *  - entity: { title, kind = 'meta' | 'azione' }
 *  - onClose(): void
 *  - onConfirm({ achieved_date, achieved_notes }): Promise<void>
 *  - showNotes: boolean (default true) — mostra la textarea note (solo per personal_goals)
 */
export default function MetaAchieveModal({ entity, onClose, onConfirm, showNotes = true }) {
  // ISO yyyy-mm-dd for today (Europe/Rome via local Date)
  const today = new Date();
  const isoToday = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;

  const [achievedDate, setAchievedDate] = useState(isoToday);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!achievedDate) { toast.error("Seleziona una data di raggiungimento"); return; }
    setBusy(true);
    try {
      await onConfirm({ achieved_date: achievedDate, achieved_notes: notes || null });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
         onClick={onClose} data-testid="meta-achieve-modal">
      <div className="bg-white rounded-2xl max-w-lg w-full p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="font-display text-xl font-semibold inline-flex items-center gap-2">
            <Award size={20} className="text-yellow-600"/> Dichiarare raggiunta
          </h3>
          <button onClick={onClose} aria-label="Chiudi"><X size={20}/></button>
        </div>
        <div className="text-sm opacity-80">"{entity.title}"</div>
        <label className="block text-sm">
          <div className="opacity-70 mb-1">Data raggiungimento</div>
          <DatePickerIT
            value={achievedDate}
            onChange={setAchievedDate}
            testId="meta-achieve-date"
            placeholder="gg/mm/aaaa"
          />
        </label>
        {showNotes && (
          <label className="block text-sm">
            <div className="opacity-70 mb-1">Note (opzionale)</div>
            <textarea className="pxos-input" rows={3} value={notes} maxLength={2000}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Cosa hai imparato lungo il cammino?"
                      data-testid="meta-achieve-notes"/>
          </label>
        )}
        <div className="flex gap-2 justify-end pt-2">
          <button onClick={onClose} className="text-sm underline opacity-70" data-testid="meta-achieve-cancel">
            Annulla
          </button>
          <button disabled={busy} onClick={submit} className="pxos-btn-primary text-sm" data-testid="meta-achieve-confirm">
            <Check size={14}/> Conferma
          </button>
        </div>
      </div>
    </div>
  );
}
